import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { API_DOMAIN } from '../config/domain';
import { Escort } from '../types';
import { fetchEscorts } from '../data/escorts';
import { getAllCities, getProvinceByCity } from '../data/cities';
import { useCountryContext } from '../contexts/CountryContext';
import { handleViewProfile } from '../utils/navigationUtils';
import { logDebug } from '../utils/log';

interface SearchBarProps {
  onSearch: (query: string, location: string) => void;
}

interface EscortSuggestion {
  type: 'escort';
  id: string;
  name: string;
  label: string;
}

interface LocationSuggestion {
  type: 'location';
  city: string;
  label: string;
}

type Suggestion = EscortSuggestion | LocationSuggestion;

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: 'bottom' | 'top';
}

const MIN_QUERY_LENGTH = 2;
const MAX_SUGGESTIONS_PER_GROUP = 5;
const DROPDOWN_MARGIN = 8;
const MIN_DROPDOWN_HEIGHT = 160;

const buildSuggestions = (text: string, escorts: Escort[]): Suggestion[] => {
  const normalized = text.trim().toLowerCase();
  if (normalized.length < MIN_QUERY_LENGTH) {
    return [];
  }

  const escortSuggestions: Suggestion[] = escorts
    .filter((escort) => escort.name.toLowerCase().includes(normalized))
    .slice(0, MAX_SUGGESTIONS_PER_GROUP)
    .map((escort) => ({
      type: 'escort',
      id: escort.id,
      name: escort.name,
      label: escort.phone ? `${escort.name} (${escort.phone})` : escort.name,
    }));

  const locationSuggestions: Suggestion[] = getAllCities()
    .filter((city) => city.toLowerCase().includes(normalized))
    .slice(0, MAX_SUGGESTIONS_PER_GROUP)
    .map((city) => {
      const province = getProvinceByCity(city);
      return {
        type: 'location',
        city,
        label: province ? `${city} (${province.region})` : city,
      };
    });

  return [...escortSuggestions, ...locationSuggestions];
};

const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const [inputValue, setInputValue] = useState('');
  const [escorts, setEscorts] = useState<Escort[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getCountryModel, selectedCountry } = useCountryContext();

  useEffect(() => {
    let cancelled = false;
    fetchEscorts(getCountryModel())
      .then((data) => {
        if (!cancelled) {
          setEscorts(data);
        }
      })
      .catch((error) => {
        logDebug('SearchBar: impossibile caricare le modelle per autocomplete:' + error);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideContainer = containerRef.current?.contains(target);
      const clickedInsideDropdown = dropdownRef.current?.contains(target);
      if (!clickedInsideContainer && !clickedInsideDropdown) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const updatePosition = () => {
      const inputEl = inputRef.current;
      if (!inputEl) {
        return;
      }
      const rect = inputEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_MARGIN;
      const spaceAbove = rect.top - DROPDOWN_MARGIN;
      const placement: DropdownPosition['placement'] =
        spaceBelow >= MIN_DROPDOWN_HEIGHT || spaceBelow >= spaceAbove ? 'bottom' : 'top';
      const maxHeight = Math.max(MIN_DROPDOWN_HEIGHT, placement === 'bottom' ? spaceBelow : spaceAbove);

      setDropdownPosition({
        top: placement === 'bottom' ? rect.bottom + DROPDOWN_MARGIN : rect.top - DROPDOWN_MARGIN,
        left: rect.left,
        width: rect.width,
        maxHeight,
        placement,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, suggestions.length]);

  const trackSearch = (searchQuery: string, searchLocation: string) => {
    const payload = {
      query: searchQuery,
      location: searchLocation,
      url: window.location.href,
      timestamp: Date.now(),
    };
    const endpoint = `${API_DOMAIN}/search-tracking/index.php`;
    try {
      navigator.sendBeacon(endpoint, JSON.stringify(payload));
    } catch (_) {
      fetch(endpoint, { method: 'POST', body: JSON.stringify(payload) }).catch(() => {});
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    const nextSuggestions = buildSuggestions(value, escorts);
    setSuggestions(nextSuggestions);
    setIsOpen(nextSuggestions.length > 0);
    setActiveIndex(-1);
  };

  const selectSuggestion = (suggestion: Suggestion) => {
    setIsOpen(false);
    setActiveIndex(-1);
    if (suggestion.type === 'escort') {
      trackSearch(suggestion.name, '');
      handleViewProfile(suggestion.id, escorts, navigate);
      return;
    }
    setInputValue(suggestion.city);
    trackSearch('', suggestion.city);
    onSearch('', suggestion.city);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsOpen(false);
    trackSearch(inputValue, '');
    onSearch(inputValue, '');
  };

  const escortSuggestions = suggestions.filter(
    (s): s is EscortSuggestion => s.type === 'escort'
  );
  const locationSuggestions = suggestions.filter(
    (s): s is LocationSuggestion => s.type === 'location'
  );

  const renderSuggestionItem = (suggestion: Suggestion) => {
    const index = suggestions.indexOf(suggestion);
    const key = suggestion.type === 'escort' ? `escort-${suggestion.id}` : `location-${suggestion.city}`;
    return (
      <li
        key={key}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => selectSuggestion(suggestion)}
        className={`px-4 py-2 cursor-pointer text-black ${
          index === activeIndex ? 'bg-pink-100' : 'hover:bg-gray-100'
        }`}
      >
        {suggestion.label}
      </li>
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col md:flex-row gap-3 w-full max-w-4xl mx-auto"
    >
      <div ref={containerRef} className="relative flex-grow">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          placeholder={t('searchBar.placeholder')}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setIsOpen(true);
            }
          }}
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent text-black"
        />
        {isOpen &&
          suggestions.length > 0 &&
          dropdownPosition &&
          createPortal(
            <ul
              ref={dropdownRef}
              style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                maxHeight: dropdownPosition.maxHeight,
                transform: dropdownPosition.placement === 'top' ? 'translateY(-100%)' : undefined,
              }}
              className="z-50 rounded-lg border border-gray-300 bg-white shadow-lg overflow-y-auto"
            >
              {escortSuggestions.length > 0 && (
                <>
                  <li className="px-4 py-1 text-xs font-semibold text-gray-500">
                    {t('searchBar.groupModels')}
                  </li>
                  {escortSuggestions.map(renderSuggestionItem)}
                </>
              )}
              {locationSuggestions.length > 0 && (
                <>
                  <li className="px-4 py-1 text-xs font-semibold text-gray-500">
                    {t('searchBar.groupLocations')}
                  </li>
                  {locationSuggestions.map(renderSuggestionItem)}
                </>
              )}
            </ul>,
            document.body
          )}
      </div>
      <button
        type="submit"
        className="py-3 px-6 bg-pink-500 hover:bg-pink-600 text-white rounded-lg transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-2"
      >
        {t('searchBar.searchButton')}
      </button>
    </form>
  );
};

export default SearchBar;
